import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Monitor, Smartphone, Tablet, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';

interface PushDevice {
  id: string;
  endpoint: string;
  user_agent: string | null;
  created_at: string;
  last_success_at: string | null;
  is_active: boolean;
}

interface PushDeviceListProps {
  devices: PushDevice[];
  onRemove: (endpoint: string) => void;
  isRemoving?: boolean;
}

// Helper to detect device type from user agent
function getDeviceInfo(userAgent: string | null): { icon: typeof Monitor; label: string } {
  if (!userAgent) return { icon: Monitor, label: 'Dispositivo desconhecido' };
  const ua = userAgent.toLowerCase();
  if (/iphone|android.*mobile/.test(ua)) return { icon: Smartphone, label: 'Celular' };
  if (/ipad|android(?!.*mobile)|tablet/.test(ua)) return { icon: Tablet, label: 'Tablet' };
  // Detect browser
  if (/chrome/.test(ua)) return { icon: Monitor, label: 'Chrome (Desktop)' };
  if (/firefox/.test(ua)) return { icon: Monitor, label: 'Firefox (Desktop)' };
  if (/safari/.test(ua)) return { icon: Monitor, label: 'Safari (Desktop)' };
  if (/edg/.test(ua)) return { icon: Monitor, label: 'Edge (Desktop)' };
  return { icon: Monitor, label: 'Desktop' };
}

export function PushDeviceList({ devices, onRemove, isRemoving }: PushDeviceListProps) {
  if (devices.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nenhum dispositivo registrado para notificações.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {devices.map((device) => {
        const { icon: DeviceIcon, label } = getDeviceInfo(device.user_agent);
        return (
          <div
            key={device.id}
            className="flex items-center justify-between rounded-md border p-3"
          >
            <div className="flex items-center gap-3 min-w-0">
              <DeviceIcon className="h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="break-words text-sm font-medium">{label}</p>
                <p className="text-xs text-muted-foreground">
                  Registrado {formatDistanceToNow(new Date(device.created_at), { locale: ptBR, addSuffix: true })}
                  {device.last_success_at && (
                    <> · Último envio {formatDistanceToNow(new Date(device.last_success_at), { locale: ptBR, addSuffix: true })}</>
                  )}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRemove(device.endpoint)}
              disabled={isRemoving}
              aria-label="Remover dispositivo"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}
